import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'

// others
import { ActionType } from '../../../context/AppContextReducer'
import { AppContext, AuthContext } from '../../../context'
import { Account, AccountCategory, useFetchAllAccountsLazyQuery } from '../../../../generated'
import { GRAPHQL_QUERY_POLICY_CACHE_FIRST, QUERY_PARAMS } from '../../../constants'

// component import
import { CardBox, CustomTabButton } from '../../../theme/styleComponents'
import { ContentHead } from '../../common/ContentHead'
import { SearchWithKeypress } from '../../common/SearchWithKeypress'
import { AddNewAccount } from '../AddNewAccount'
import { anchorOrigin, flexCenter, flexJustifySpaceBetween, transformOrigin } from '../../../theme/styleConstants'
import { EmptyData } from '../../common/EmptyData'
import ChartsOfAccountsListing from './ChartsOfAccountsListing'
import CategoriesBalances from './CategoriesBalances'
import AccountCategoryDropdown from './AccountCategoryDropdown'
import TransferFunds from '../TransferFunds'

export const ChartsOfAccounts = () => {
  const { dispatch: reducerDispatch, pageLimit, currentPage, searchValue } = useContext(AppContext)
  const { currentUser } = useContext(AuthContext)
  const [tab, setTab] = useState<'active' | 'archived'>('active')
  const [accountListType, setAccountListType] = useState('bank')
  const [refetchCategoriesBalances, setRefetchCategoriesBalances] = useState(new Date().getTime())

  const [category, setCategory] = useState<AccountCategory | null>(null)
  const [chartsofAccounts, setChartsofAccounts] = useState<(Account | null)[]>([])

  const [debouncedSearchTerm] = useDebounce(searchValue, 500)

  const [fetchAllAccounts, { loading, refetch }] = useFetchAllAccountsLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,

    onError() {
      reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
    },

    onCompleted(data) {
      const {
        fetchAllAccounts: { accounts, pagination }
      } = data || {}

      if (accounts) {
        setChartsofAccounts(accounts as Account[])
      }

      if (pagination) {
        const { totalPages } = pagination

        totalPages && reducerDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages })
      }
    }
  })

  const handleCategoryChange = (value: AccountCategory | null) => {
    setCategory(value)
    reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
  }

  const handleAccountListTypeChange = (event: SelectChangeEvent) => {
    const { value } = event.target

    if (value) setAccountListType(value)
  }

  useEffect(() => {
    fetchAllAccounts({
      variables: {
        accountsInput: {
          paginationOptions: {
            page: currentPage,
            limit: pageLimit
          },
          searchValue: debouncedSearchTerm,
          active: tab === 'active',
          category: category,
          isBankAccount: accountListType === QUERY_PARAMS.ALL ? undefined : accountListType === 'bank' ? true : false
        }
      }
    })
  }, [currentUser, currentPage, debouncedSearchTerm, tab, category, fetchAllAccounts, pageLimit, accountListType])

  useEffect(() => {
    return () => {
      reducerDispatch({ type: ActionType.SET_SEARCH_VALUE, searchValue: '' })
      reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
      reducerDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: 1 })
      reducerDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: 0 })
    }
    // eslint-disable-next-line
  }, [])

  const isEmpty = chartsofAccounts?.length === 0 && !loading

  return (
    <>
      {/* <Box display="flex" flexDirection="column" mt={2}>
        <Typography variant="h5">Charts of Accounts</Typography>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <CategoriesBalances triggerRefetch={refetchCategoriesBalances} />
        </Box>
      </Box> */}

      <CardBox marginTop={3}>
        <ContentHead
          title="Accounts"
          buttonText="Add Account"
          customButton={() => (
            <TransferFunds
              refetch={() => {
                refetch()
                setRefetchCategoriesBalances(new Date().getTime())
              }}
            />
          )}
        />

        <Box sx={{ px: 2, pt: 3 }}>
          <CustomTabButton
            isLeftItem
            className={tab === 'active' ? 'MuiTab-Custom Mui-selected' : 'MuiTab-Custom'}
            onClick={() => setTab('active')}
          >
            Active Accounts
          </CustomTabButton>

          <CustomTabButton
            className={tab === 'archived' ? 'MuiTab-Custom Mui-selected' : 'MuiTab-Custom'}
            onClick={() => setTab('archived')}
          >
            Archived Accounts
          </CustomTabButton>
        </Box>

        <Box sx={{ ...flexCenter, ...flexJustifySpaceBetween, flexWrap: 'wrap', px: 2, pt: 5, pb: 3 }}>
          <Box display={'flex'} gap={2}>
            <AccountCategoryDropdown value={category} onChange={handleCategoryChange} sx={{ minWidth: '250px' }} />
            <FormControl>
              <InputLabel id="accounts">Account List:</InputLabel>
              <Select
                labelId="A/D"
                label={'Account List:'}
                id="accounts"
                onChange={handleAccountListTypeChange}
                displayEmpty={true}
                value={accountListType ?? 'bank'}
                sx={{ width: '200px' }}
                MenuProps={{
                  anchorOrigin,
                  transformOrigin,
                  style: {
                    maxHeight: 400
                  }
                }}
              >
                <MenuItem value={QUERY_PARAMS.ALL}>All</MenuItem>
                <MenuItem value={'bank'}>Bank Accounts</MenuItem>
                <MenuItem value={'gl'}>GL Account</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ minWidth: '300px' }}>
            <SearchWithKeypress searchPlaceholder={'Search'} />
          </Box>
        </Box>

        {isEmpty ? (
          <Box sx={{ ...flexCenter, height: 'calc(100vh - 400px)' }}>
            <EmptyData title={'No accounts to show!'} />
          </Box>
        ) : (
          <ChartsOfAccountsListing accounts={chartsofAccounts} loading={loading} refetch={refetch} />
        )}
      </CardBox>

      <AddNewAccount refetch={refetch} />
    </>
  )
}
